import React, { useState } from 'react'
import { User } from 'lucide-react'
import { clsx } from 'clsx'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface AvatarProps {
  src?: string
  name?: string
  size?: AvatarSize
  className?: string
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'w-8 h-8',
  sm: 'w-9 h-9',
  md: 'w-12 h-12',
  lg: 'w-14 h-14',
  xl: 'w-16 h-16'
}

const iconSizeClasses: Record<AvatarSize, string> = {
  xs: 'w-4 h-4',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-7 h-7',
  xl: 'w-8 h-8'
}

const initialsSizeClasses: Record<AvatarSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl'
}

// Warm, alternating identity colors so avatars read as people, not UI chrome
const initialsPalette = [
  { bg: 'bg-primary-500', text: 'text-white' },
  { bg: 'bg-accent-500', text: 'text-white' },
  { bg: 'bg-primary-700', text: 'text-white' },
  { bg: 'bg-accent-700', text: 'text-white' },
]

function paletteForName(name: string) {
  const hash = name.split('').reduce((acc, ch) => acc + (ch.codePointAt(0) ?? 0), 0)
  return initialsPalette[hash % initialsPalette.length]
}

function getInitial(name?: string) {
  if (!name) return undefined
  const trimmed = name.trim()
  return trimmed.length ? trimmed[0].toUpperCase() : undefined
}

export const Avatar: React.FC<AvatarProps> = ({ src, name, size = 'md', className }) => {
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined)

  const ringWrapper = clsx(
    sizeClasses[size],
    'rounded-full p-[2px] bg-gradient-to-br from-primary-400 to-accent-400 flex-shrink-0',
    className
  )

  if (src && src !== failedSrc) {
    return (
      <div className={ringWrapper}>
        <img
          src={src}
          alt={name || ''}
          onError={() => setFailedSrc(src)}
          className="w-full h-full rounded-full object-cover border-2 border-white"
        />
      </div>
    )
  }

  const initial = getInitial(name)

  if (initial) {
    const colors = paletteForName(name!)
    return (
      <div className={ringWrapper}>
        <div
          className={clsx(
            colors.bg,
            colors.text,
            'w-full h-full rounded-full flex items-center justify-center font-display border-2 border-white',
            initialsSizeClasses[size]
          )}
        >
          {initial}
        </div>
      </div>
    )
  }

  return (
    <div className={ringWrapper}>
      <div className="w-full h-full rounded-full bg-primary-100 flex items-center justify-center border-2 border-white">
        <User className={clsx(iconSizeClasses[size], 'text-primary-600')} />
      </div>
    </div>
  )
}

export default Avatar
